import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import * as React from "react";
import { Button } from "../Button";
import styles from "./Navbar.module.css";

interface SafeAuthButtonsProps {
  variant: "desktop" | "mobile";
}

const SignInKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    (e.currentTarget as HTMLElement).click();
  }
};

const DesktopClerkButtons: React.FC = () => (
  <>
    <SignInButton mode="modal">
      <span
        role="button"
        tabIndex={0}
        className={styles.signInLink}
        onKeyDown={SignInKeyDown}
      >
        Sign in
      </span>
    </SignInButton>
    <SignUpButton mode="modal">
      <Button
        as="span"
        variant="secondary"
        size="small"
        role="button"
        tabIndex={0}
        onKeyDown={SignInKeyDown}
      >
        Try for Free
      </Button>
    </SignUpButton>
  </>
);

const MobileClerkButtons: React.FC = () => (
  <>
    <SignUpButton mode="modal">
      <Button
        as="span"
        variant="primary"
        size="small"
        className={styles.mobileSignInButton}
        role="button"
        tabIndex={0}
        onKeyDown={SignInKeyDown}
      >
        Try for Free
      </Button>
    </SignUpButton>
    <SignInButton mode="modal">
      <Button
        as="span"
        variant="secondary"
        size="small"
        className={styles.mobileCTAButton}
        role="button"
        tabIndex={0}
        onKeyDown={SignInKeyDown}
      >
        Sign in
      </Button>
    </SignInButton>
  </>
);

const DesktopFallbackButtons: React.FC = () => (
  <>
    <a href="/auth/login" className={styles.signInLink}>
      Sign in
    </a>
    <Button as="a" href="/auth/signup" variant="secondary" size="small">
      Try for Free
    </Button>
  </>
);

const MobileFallbackButtons: React.FC = () => (
  <>
    <Button
      as="a"
      href="/auth/signup"
      variant="primary"
      size="small"
      className={styles.mobileSignInButton}
    >
      Try for Free
    </Button>
    <Button
      as="a"
      href="/auth/login"
      variant="secondary"
      size="small"
      className={styles.mobileCTAButton}
    >
      Sign in
    </Button>
  </>
);

class ClerkErrorBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { hasError: boolean }
> {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  constructor(props: { fallback: React.ReactNode; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export const SafeAuthButtons: React.FC<SafeAuthButtonsProps> = ({
  variant,
}) => {
  if (variant === "desktop") {
    return (
      <ClerkErrorBoundary fallback={<DesktopFallbackButtons />}>
        <DesktopClerkButtons />
      </ClerkErrorBoundary>
    );
  }

  return (
    <ClerkErrorBoundary fallback={<MobileFallbackButtons />}>
      <MobileClerkButtons />
    </ClerkErrorBoundary>
  );
};
